import type { T_JsonValue } from "@lattice/core-lib/types/chat-protocol";
import {
  isChatV8UnknownEventPayload,
  z_ChatV8ChartWidgetSnapshotData,
  z_ChatV8FeedWidgetData,
  z_ChatV8InsightWidgetData,
  z_ChatV8MessagePartWidgetPayload,
  z_ChatV8PresentableTableWidget,
  z_ChatV8TableWidget,
  z_ChatV8WidgetSnapshot,
  type T_ChatV8EventPayload,
  type T_ChatV8ChartWidgetSnapshotData,
  type T_ChatV8FeedWidgetData,
  type T_ChatV8InsightWidgetData,
  type T_ChatV8PortfolioRatingWidgetData,
  type T_ChatV8StockWidgetData,
  type T_ChatV8TableWidget,
  type T_ChatV8UpcomingEventsWidgetData,
  type T_ChatV8WidgetType,
} from "@lattice/core-lib/types/chat-v8";
import { parseChatV8StockWidgetSnapshotData } from "@lattice/shared-frontend/chat-v8";

import { prepareTablePresentation } from "./table-presentation";

export type T_PlacedWidgetInput = {
  payload: T_ChatV8EventPayload;
};

type T_PlacedWidgetSnapshotMeta = {
  snapshotStatus: "completed" | "partial";
  state: string;
  warningCount: number;
  warnings: string[];
  widgetType: T_ChatV8WidgetType;
};

export type T_PlacedWidgetParseResult =
  | {
      kind: "loading";
      state: string;
      title: string;
      widgetType: T_ChatV8WidgetType;
    }
  | {
      kind: "unavailable";
      state: string;
      title: string;
      warningCount: number;
      widgetType: T_ChatV8WidgetType;
    }
  | (T_PlacedWidgetSnapshotMeta & {
      data: T_ChatV8ChartWidgetSnapshotData;
      kind: "chart";
    })
  | (T_PlacedWidgetSnapshotMeta & {
      data: T_ChatV8StockWidgetData;
      kind: "stock";
    })
  | (T_PlacedWidgetSnapshotMeta & {
      copyMarkdown: string;
      data: T_ChatV8TableWidget;
      hiddenRowCount: number;
      kind: "table";
    })
  // Rich presentations carried inside a table artifact (RAI-1092/RAI-1097).
  // Renderers show the rich card INSTEAD of the table; a malformed
  // presentation degrades to kind "table", never to "unavailable".
  | (T_PlacedWidgetSnapshotMeta & {
      data: T_ChatV8PortfolioRatingWidgetData;
      kind: "portfolio_rating";
    })
  | (T_PlacedWidgetSnapshotMeta & {
      data: T_ChatV8UpcomingEventsWidgetData;
      kind: "upcoming_events";
    })
  | (T_PlacedWidgetSnapshotMeta & {
      data: T_ChatV8InsightWidgetData;
      kind: "insight";
    })
  | (T_PlacedWidgetSnapshotMeta & {
      data: T_ChatV8FeedWidgetData;
      kind: "feed";
    })
  | {
      kind: "unknown";
      state: string;
      widgetType: string;
    };

export function parsePlacedWidget(
  input: T_PlacedWidgetInput,
): T_PlacedWidgetParseResult {
  const parsedPayload = z_ChatV8MessagePartWidgetPayload.safeParse(
    input.payload,
  );
  if (!parsedPayload.success) {
    const state = getUnknownPayloadString(input.payload, "state") || "started";
    const widgetType = getUnknownPayloadString(input.payload, "widgetType");
    return { kind: "unknown", state, widgetType };
  }
  const payload = parsedPayload.data;
  const widgetType = payload.widgetType;
  const state = payload.state;

  const title = getPlacedWidgetTitle(widgetType, payload.snapshot);
  const rawSnapshot = payload.snapshot;
  if (rawSnapshot === undefined) {
    if (state === "failed") {
      return {
        kind: "unavailable",
        state,
        title,
        warningCount: 0,
        widgetType,
      };
    }
    return { kind: "loading", state, title, widgetType };
  }

  const parsedSnapshot = z_ChatV8WidgetSnapshot.safeParse(rawSnapshot);
  if (
    !parsedSnapshot.success ||
    parsedSnapshot.data.widgetType !== widgetType
  ) {
    return {
      kind: "unavailable",
      state,
      title,
      warningCount: 0,
      widgetType,
    };
  }

  const snapshot = parsedSnapshot.data;
  const warningCount = snapshot.warnings?.length ?? 0;
  if (snapshot.status === "unavailable") {
    return { kind: "unavailable", state, title, warningCount, widgetType };
  }

  const meta: T_PlacedWidgetSnapshotMeta = {
    snapshotStatus: snapshot.status,
    state,
    warningCount,
    warnings: snapshot.warnings ?? [],
    widgetType,
  };

  switch (widgetType) {
    case "chart": {
      const parsed = z_ChatV8ChartWidgetSnapshotData.safeParse(snapshot.data);
      if (!parsed.success) {
        return { kind: "unavailable", state, title, warningCount, widgetType };
      }
      return { ...meta, data: parsed.data, kind: "chart" };
    }
    case "stock": {
      const parsed = parseChatV8StockWidgetSnapshotData(snapshot.data);
      if (!parsed?.data) {
        return { kind: "unavailable", state, title, warningCount, widgetType };
      }
      return { ...meta, data: parsed.data, kind: "stock" };
    }
    case "table": {
      const parsed = z_ChatV8TableWidget.safeParse(snapshot.data);
      if (!parsed.success) {
        return { kind: "unavailable", state, title, warningCount, widgetType };
      }
      // Presentation upgrade: parsed separately so a malformed presentation
      // can never cost the user the table fallback (fail-open).
      const presentable = z_ChatV8PresentableTableWidget.safeParse(
        snapshot.data,
      );
      if (presentable.success && presentable.data.presentation) {
        const upgrade = presentable.data.presentation;
        if (upgrade.kind === "portfolio_rating") {
          return { ...meta, data: upgrade.data, kind: "portfolio_rating" };
        }
        return { ...meta, data: upgrade.data, kind: "upcoming_events" };
      }
      const presentation = prepareTablePresentation(parsed.data);
      return {
        ...meta,
        ...presentation,
        kind: "table",
      };
    }
    case "insight": {
      const parsed = z_ChatV8InsightWidgetData.safeParse(snapshot.data);
      if (!parsed.success) {
        return { kind: "unavailable", state, title, warningCount, widgetType };
      }
      return { ...meta, data: parsed.data, kind: "insight" };
    }
    case "feed_widget": {
      const parsed = z_ChatV8FeedWidgetData.safeParse(snapshot.data);
      if (!parsed.success) {
        return { kind: "unavailable", state, title, warningCount, widgetType };
      }
      return { ...meta, data: parsed.data, kind: "feed" };
    }
  }
}

function getUnknownPayloadString(
  payload: T_ChatV8EventPayload,
  key: string,
): string {
  if (!isChatV8UnknownEventPayload(payload)) return "";
  const value = payload[key];
  return typeof value === "string" ? value : "";
}

export function getPlacedWidgetTypeLabel(widgetType: string): string {
  switch (widgetType) {
    case "chart":
      return "Chart";
    case "stock":
      return "Stock";
    case "table":
      return "Table";
    case "feed_widget":
      return "Feed";
    case "insight":
      return "Insight";
    default: {
      const cleaned = widgetType.replace(/_/g, " ").trim();
      return cleaned.length > 0
        ? cleaned.charAt(0).toUpperCase() + cleaned.slice(1)
        : "Widget";
    }
  }
}

function getPlacedWidgetTitle(
  widgetType: T_ChatV8WidgetType,
  rawSnapshot: T_JsonValue | undefined,
): string {
  if (isRecord(rawSnapshot)) {
    const data = rawSnapshot["data"];
    if (isRecord(data)) {
      const title = data["title"];
      if (typeof title === "string" && title.trim().length > 0) {
        return title;
      }
      const chart = data["chart"];
      if (isRecord(chart)) {
        const chartTitle = chart["title"];
        if (typeof chartTitle === "string" && chartTitle.trim().length > 0) {
          return chartTitle;
        }
      }
      const descriptor = data["descriptor"];
      if (isRecord(descriptor)) {
        const descriptorTitle = descriptor["title"];
        if (
          typeof descriptorTitle === "string" &&
          descriptorTitle.trim().length > 0
        ) {
          return descriptorTitle;
        }
      }
    }
  }
  return `${getPlacedWidgetTypeLabel(widgetType)} widget`;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}
